import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import Layout from '../AppLayout';

const PageContainer = styled.div`
  padding: 120px 40px 40px;
  max-width: 1400px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  margin-bottom: 40px;
`;

const PageTitle = styled(motion.h1)`
  font-family: 'Orbitron', sans-serif;
  font-size: 36px;
  background: linear-gradient(135deg, var(--primary), var(--accent));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 10px;
`;

const PageDesc = styled(motion.p)`
  color: var(--text-secondary);
  font-size: 16px;
`;

const TabContainer = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 30px;
  border-bottom: 1px solid var(--border-color);
  padding-bottom: 10px;
`;

const Tab = styled.button`
  background: ${props => props.$active ? 'rgba(0, 240, 255, 0.15)' : 'transparent'};
  color: ${props => props.$active ? 'var(--primary)' : 'var(--text-secondary)'};
  border: 1px solid ${props => props.$active ? 'var(--primary)' : 'var(--border-color)'};
  padding: 12px 24px;
  border-radius: 8px;
  font-family: 'Orbitron', sans-serif;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: var(--primary);
    color: var(--primary);
  }
`;

const PlatformSelector = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 30px;
  padding: 15px;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid var(--border-color);
  border-radius: 8px;
`;

const PlatformLabel = styled.span`
  color: var(--text-primary);
  font-weight: 500;
  font-size: 14px;
`;

const PlatformButton = styled.button`
  background: ${props => props.$active ? 'rgba(0, 240, 255, 0.2)' : 'rgba(255, 255, 255, 0.05)'};
  color: ${props => props.$active ? 'var(--primary)' : 'var(--text-secondary)'};
  border: 1px solid ${props => props.$active ? 'var(--primary)' : 'var(--border-color)'};
  padding: 8px 16px;
  border-radius: 6px;
  font-family: 'Orbitron', sans-serif;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(0, 240, 255, 0.15);
    border-color: var(--primary);
  }
`;

const Card = styled(motion.div)`
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 20px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
`;

const ModelCard = styled(motion.div)`
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.3s ease;
  cursor: pointer;
  
  &:hover {
    border-color: var(--primary);
    transform: translateY(-4px);
    box-shadow: 0 10px 30px rgba(0, 240, 255, 0.15);
  }
  
  ${props => props.selected && `
    border-color: var(--primary);
    box-shadow: 0 0 20px rgba(0, 240, 255, 0.3);
  `}
`;

const ModelImage = styled.div`
  width: 100%;
  height: 180px;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 48px;
  position: relative;
  overflow: hidden;
`;

const ModelInfo = styled.div`
  padding: 16px;
  
  h4 {
    color: var(--text-primary);
    font-size: 16px;
    margin-bottom: 8px;
  }
  
  p {
    color: var(--text-secondary);
    font-size: 12px;
    margin: 0;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  color: var(--text-secondary);
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const Input = styled.input`
  width: 100%;
  padding: 14px 18px;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  color: var(--text-primary);
  font-size: 16px;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 15px rgba(0, 240, 255, 0.2);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 14px 18px;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  color: var(--text-primary);
  font-size: 14px;
  min-height: 100px;
  resize: vertical;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 15px rgba(0, 240, 255, 0.2);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 14px 18px;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  color: var(--text-primary);
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: var(--primary);
  }
  
  option {
    background: #1a1a2e;
  }
`;

const Button = styled(motion.button)`
  background: linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%);
  color: #fff;
  font-family: 'Orbitron', sans-serif;
  font-size: 14px;
  font-weight: 600;
  padding: 14px 30px;
  border-radius: 8px;
  text-transform: uppercase;
  letter-spacing: 2px;
  border: none;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  
  background: ${props => {
    switch(props.status) {
      case 2: return 'rgba(0, 255, 136, 0.15)';
      case 1: return 'rgba(255, 193, 7, 0.15)';
      case 3: return 'rgba(255, 51, 102, 0.15)';
      default: return 'rgba(160, 160, 176, 0.15)';
    }
  }};
  color: ${props => {
    switch(props.status) {
      case 2: return '#00ff88';
      case 1: return '#ffc107';
      case 3: return '#ff3366';
      default: return '#a0a0b0';
    }
  }};
`;

const ResultBox = styled.div`
  background: ${props => props.error ? 'rgba(255, 51, 102, 0.1)' : 'rgba(0, 255, 136, 0.1)'};
  border: 1px solid ${props => props.error ? 'rgba(255, 51, 102, 0.3)' : 'rgba(0, 255, 136, 0.3)'};
  border-radius: 8px;
  padding: 16px;
  margin-top: 20px;
  
  pre {
    color: ${props => props.error ? '#ff3366' : '#00ff88'};
    font-size: 13px;
    font-family: monospace;
    white-space: pre-wrap;
    word-break: break-all;
    margin: 0;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: var(--text-secondary);
  
  h3 {
    font-family: 'Orbitron', sans-serif;
    margin-bottom: 10px;
  }
  
  p {
    font-size: 14px;
    margin-bottom: 20px;
  }
`;

const API_BASE_URL = '/api/v1';

const navigateToVideo = (model) => {
  const params = new URLSearchParams({
    model_id: model.id,
    model_name: model.name,
    platform_code: model.platform_code || 'Pro',
    video_url: model.video_oss_url || ''
  });
  window.location.href = `/video?${params.toString()}`;
};

const ClonePage = () => {
  const [activeTab, setActiveTab] = useState('public');
  const [publicModels, setPublicModels] = useState([]);
  const [myClones, setMyClones] = useState([]);
  const [selectedModel, setSelectedModel] = useState(null);
  const [modelSource, setModelSource] = useState(null); // 'public' = 公模, 'private' = 我的克隆
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [selectedPlatform, setSelectedPlatform] = useState('Pro'); // Pro商业版或VPro高清版
  
  const [cloneForm, setCloneForm] = useState({
    video_oss_url: '',
    name: '',
    sex: 1,
    description: '',
    agree: false
  });
  
  const [createForm, setCreateForm] = useState({
    model_type: 1,
    model_id: null,
    voice_type: '1',
    voice_id: null,
    text: '',
    voice_source: 'text',
    audio_oss_url: '',
    task_name: ''
  });
  
  const [voiceList, setVoiceList] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [videos, setVideos] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [voiceHistory, setVoiceHistory] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [videoPlayer, setVideoPlayer] = useState({
    isOpen: false,
    videoUrl: null,
    title: null
  });

  const openVideoPlayer = (videoUrl, title) => {
    setVideoPlayer({
      isOpen: true,
      videoUrl,
      title
    });
  };

  const closeVideoPlayer = () => {
    setVideoPlayer({
      isOpen: false,
      videoUrl: null,
      title: null
    });
  };

  const openVideoPreview = (video) => {
    setSelectedVideo(video);
    setIsVideoModalOpen(true);
  };

  useEffect(() => {
    fetchPublicModels();
    fetchVoiceList();
    fetchMyClones();
    fetchTasks();
    fetchVideos();
    fetchVoiceHistory();
  }, []);

  useEffect(() => {
    if (activeTab === 'public') {
      fetchPublicModels();
    } else if (activeTab === 'myclones') {
      fetchMyClones();
    } else if (activeTab === 'preview') {
      fetchVideos();
    }
  }, [selectedPlatform, activeTab]);

  const fetchVoiceHistory = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API_BASE_URL}/voice/history`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.data.code === 1 && response.data.data) {
        setVoiceHistory(response.data.data);
      }
    } catch (error) {
      console.error('获取声音历史记录失败:', error);
      setMessage({ type: 'error', text: '获取声音历史记录失败，请稍后重试' });
    }
  };

  const handleVoiceUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploading(true);
    setUploadProgress(0);
    setMessage(null);

    const token = localStorage.getItem('token');
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API_BASE_URL}/voice/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percent);
        }
      });

      if (response.data.code === 1) {
        setCreateForm({...createForm, audio_oss_url: response.data.data.url});
        setMessage({ type: 'success', text: '声音上传成功！' });
        // 刷新历史记录
        fetchVoiceHistory();
      } else {
        setMessage({ type: 'error', text: response.data.msg || '上传失败' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: `上传失败: ${error.message}` });
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const selectHistoryVoice = (voice) => {
    setCreateForm({...createForm, audio_oss_url: voice.url});
    setMessage({ type: 'success', text: `已选择历史声音: ${voice.filename}` });
  };

  const fetchPublicModels = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API_BASE_URL}/digital_human/public/models`, {
        params: {
          platform_code: selectedPlatform
        },
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.data.code === 1 && response.data.data) {
        setPublicModels(response.data.data);
        setMessage({ type: 'success', text: `已加载${selectedPlatform === 'Pro' ? 'Pro商业版' : 'VPro高清版'}公模数据` });
      } else {
        setMessage({ type: 'warning', text: '暂无公模数据' });
      }
    } catch (error) {
      console.error('获取公模列表失败:', error);
      setMessage({ type: 'error', text: '获取公模列表失败，请稍后重试' });
    } finally {
      setLoading(false);
    }
  };

  const fetchMyClones = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API_BASE_URL}/digital_human/my-models`, {
        params: {
          platform_code: selectedPlatform
        },
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.data.code === 1 && response.data.data) {
        setMyClones(response.data.data);
        setMessage({ type: 'success', text: `已加载${selectedPlatform === 'Pro' ? 'Pro商业版' : 'VPro高清版'}我的形象数据` });
      } else {
        setMessage({ type: 'warning', text: '暂无克隆形象数据' });
      }
    } catch (error) {
      console.error('获取我的克隆形象失败:', error);
      setMessage({ type: 'error', text: '获取克隆形象失败，请稍后重试' });
    } finally {
      setLoading(false);
    }
  };

  const fetchTasks = async () => {
    setLoading(true);
    try {
      // 从API获取真实的任务数据
      const token = localStorage.getItem('token');
      if (!token) {
        console.warn('未登录，使用模拟任务数据');
        const mockTasks = getMockTasks();
        setTasks(mockTasks);
        return;
      }
      
      const response = await axios.get(`${API_BASE_URL}/user/video/tasks`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data.code === 1 && response.data.data) {
        // 处理任务数据，确保有所有需要的字段
        const tasksData = response.data.data.map(task => ({
          id: task.id || task.task_id || 0,
          task_name: task.task_name || '未命名任务',
          generate_channel: task.generate_channel || task.platform_name || 'pro商业版',
          voice: task.voice || task.voice_name || 'AI配音',
          status: task.status || 0,
          status_text: task.status_text || getStatusText(task.status),
          created_time: task.created_time || task.create_time || '未知时间',
          thumbnail: task.thumbnail || task.video_cover || 'https://neeko-copilot.bytedance.net/api/text2image?prompt=digital%20human%20video&size=512x512',
          // 新增字段
          model_name: task.model_name || '未知形象',
          model_video_url: task.model_video_url || task.video_oss_url || '',
          voice_url: task.voice_url || '',
          video_url: task.video_url || task.result_video_url || '',
          submit_time: task.submit_time || task.created_time || '未知时间'
        }));
        setTasks(tasksData);
      } else {
        console.error('获取任务列表失败:', response.data.msg);
        // 如果API失败，使用模拟数据
        const mockTasks = getMockTasks();
        setTasks(mockTasks);
      }
    } catch (error) {
      console.error('获取任务列表失败:', error);
      // 如果API失败，使用模拟数据
      const mockTasks = getMockTasks();
      setTasks(mockTasks);
    } finally {
      setLoading(false);
    }
  };

  // 辅助函数：获取状态文本
  const getStatusText = (status) => {
    switch(status) {
      case 0: return '等待中';
      case 1: return '处理中';
      case 2: return '已完成';
      case 3: return '失败';
      default: return '未知状态';
    }
  };

  // 模拟任务数据（备用）
  const getMockTasks = () => {
    return [
      {
        id: 27,
        task_name: '1',
        generate_channel: 'pro商业版',
        voice: 'AI配音',
        status: 2,
        status_text: '任务完成',
        created_time: '2026-04-16 09:25:17',
        thumbnail: 'https://neeko-copilot.bytedance.net/api/text2image?prompt=professional%20business%20man%20wearing%20cap&size=512x512',
        model_name: '专业男性',
        model_video_url: 'https://example.com/model1.mp4',
        voice_url: 'https://example.com/voice1.wav',
        video_url: 'https://example.com/video1.mp4',
        submit_time: '2026-04-16 09:25:17'
      },
      {
        id: 26,
        task_name: '紫替',
        generate_channel: 'pro商业版',
        voice: 'AI配音',
        status: 2,
        status_text: '任务完成',
        created_time: '2026-03-19 11:59:09',
        thumbnail: 'https://neeko-copilot.bytedance.net/api/text2image?prompt=professional%20business%20woman&size=512x512',
        model_name: '专业女性',
        model_video_url: 'https://example.com/model2.mp4',
        voice_url: 'https://example.com/voice2.wav',
        video_url: 'https://example.com/video2.mp4',
        submit_time: '2026-03-19 11:59:09'
      },
      {
        id: 25,
        task_name: '1',
        generate_channel: 'pro商业版',
        voice: 'AI配音',
        status: 2,
        status_text: '任务完成',
        created_time: '2026-02-25 16:06:33',
        thumbnail: 'https://neeko-copilot.bytedance.net/api/text2image?prompt=professional%20business%20man%20wearing%20cap&size=512x512',
        model_name: '专业男性',
        model_video_url: 'https://example.com/model3.mp4',
        voice_url: 'https://example.com/voice3.wav',
        video_url: 'https://example.com/video3.mp4',
        submit_time: '2026-02-25 16:06:33'
      }
    ];
  };

  const fetchVideos = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setMessage({ type: 'warning', text: '请先登录以查看您的视频' });
        setLoading(false);
        return;
      }
      
      const response = await axios.get(`${API_BASE_URL}/user/video/tasks`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('[DEBUG] 成片列表API响应:', response.data);
      
      if (response.data.code === 1 && response.data.data) {
        const taskList = Array.isArray(response.data.data) ? response.data.data : [];
        
        const completedTasks = taskList.filter(task => task.status === 2);
        
        const formattedVideos = completedTasks.map(task => ({
          id: task.id || 0,
          title: task.task_name || task.task_name || '未命名视频',
          duration: task.duration || '0',
          digital_human: task.model_name || '未知形象',
          thumbnail: task.thumbnail || (task.video_url ? `${task.video_url.split('?')[0]}?x-oss-process=video/snapshot,t_0,f_jpg,m_fast` : ''),
          video_url: task.video_url || '',
          submit_time: task.created_at || task.updated_at || '',
          platform_code: task.platform_code || 'Pro'
        }));
        
        console.log('[DEBUG] 筛选出的已完成任务:', formattedVideos);
        
        if (formattedVideos.length > 0) {
          setVideos(formattedVideos);
          setMessage({ type: 'success', text: `已加载 ${formattedVideos.length} 个成片` });
        } else {
          setMessage({ type: 'info', text: '暂无完成的视频，请先创建视频任务' });
          setVideos([]);
        }
      } else {
        console.error('获取成片列表失败:', response.data.msg);
        setMessage({ type: 'error', text: response.data.msg || '获取成片列表失败' });
      }
    } catch (error) {
      console.error('获取成片列表失败:', error);
      if (error.response) {
        console.error('错误响应:', error.response.data);
      }
      setMessage({ type: 'error', text: '获取成片列表失败，请稍后重试' });
    } finally {
      setLoading(false);
    }
  };

  const fetchVoiceList = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API_BASE_URL}/voice/list`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.data.code === 1 && response.data.data) {
        setVoiceList(response.data.data);
      }
    } catch (error) {
      console.error('获取声音列表失败:', error);
      setMessage({ type: 'error', text: '获取声音列表失败，请稍后重试' });
    }
  };

  const handleCloneSubmit = async (e) => {
    e.preventDefault();
    if (!cloneForm.video_oss_url || !cloneForm.name) {
      setMessage({ type: 'error', text: '请填写视频地址和名称' });
      return;
    }
    
    setLoading(true);
    setMessage(null);
    
    const token = localStorage.getItem('token');
    try {
      const response = await axios.post(`${API_BASE_URL}/digital_human/clone`, cloneForm, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.data.code === 1) {
        setMessage({ type: 'success', text: '克隆任务已提交，请耐心等待...' });
        setCloneForm({ video_oss_url: '', name: '', sex: 1, description: '' });
      } else {
        setMessage({ type: 'error', text: response.data.msg || '克隆失败' });
      }
    } catch (error) {
      console.error('[ERROR] 视频生成失败:', error);
      
      let errorText = '请求失败，请稍后重试';
      
      if (error.response?.data) {
        const data = error.response.data;
        if (data.detail) {
          if (typeof data.detail === 'string') {
            errorText = data.detail;
          } else if (typeof data.detail === 'object') {
            errorText = data.detail.msg || data.detail.message || JSON.stringify(data.detail);
          }
        } else if (data.msg) {
          errorText = data.msg;
        } else if (data.message) {
          errorText = data.message;
        }
      } else if (error.message) {
        errorText = error.message;
      }
      
      setMessage({ type: 'error', text: errorText });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateVideo = async () => {
    if (!selectedModel) {
      setMessage({ type: 'error', text: '请选择数字人形象' });
      return;
    }
    if (createForm.voice_source === 'text' && !createForm.text) {
      setMessage({ type: 'error', text: '请输入文案内容' });
      return;
    }
    
    if ((createForm.voice_source === 'upload' || createForm.voice_source === 'history') && !createForm.audio_oss_url) {
      setMessage({ type: 'error', text: '请上传声音文件或选择历史声音' });
      return;
    }
    
    setLoading(true);
    setMessage(null);
    
    try {
      // 确定platform_code：优先使用selectedModel中的platform_code，如果没有则使用selectedPlatform
      const platformCode = selectedModel.platform_code || selectedPlatform;
      
      // 使用modelSource判断是公模还是我的克隆
      // 'public' = 公模使用model_type=1, 'private' = 我的克隆使用model_type=2
      const modelType = modelSource === 'private' ? '2' : '1';
      
      console.log('[DEBUG] 提交视频合成请求:', {
        modelType,
        model_id: selectedModel.id,
        platform_code: platformCode,
        modelSource: modelSource
      });
      
      const payload = {
        model_type: modelType,
        model_id: selectedModel.id,
        platform_code: platformCode,
      };
      
      if (createForm.voice_source === 'text') {
        payload.text = createForm.text;
        payload.voice_type = createForm.voice_type || '1';
        payload.voice_id = createForm.voice_id || 42; // 确保voice_id始终存在，使用存在的声音模型ID
        payload.voice_model_type = '1'; // 确保voice_model_type始终存在
      } else if (createForm.voice_source === 'upload' || createForm.voice_source === 'history') {
        payload.voice_type = '2'; // 上传的声音
        payload.audio_oss_url = createForm.audio_oss_url;
        // 对于上传的声音，也可以提供文本作为字幕
        if (createForm.text) {
          payload.text = createForm.text;
        }
      }
      
      const token = localStorage.getItem('token');
      
      const response = await axios.post(`${API_BASE_URL}/user/video/merge`, payload, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.data.code === 1) {
        // 检查data的类型，可能是数字或数组
        let taskId = '未知';
        if (typeof response.data.data === 'number') {
          taskId = response.data.data;
        } else if (Array.isArray(response.data.data) && response.data.data.length > 0) {
          taskId = response.data.data[0]?.id || '未知';
        } else if (response.data.data?.id) {
          taskId = response.data.data.id;
        }
        setMessage({ type: 'success', text: `视频生成成功！任务ID: ${taskId}` });
      } else {
        setMessage({ type: 'error', text: response.data.msg || '生成失败' });
      }
    } catch (error) {
      // 确保message.text始终是字符串
      let errorText = '请求失败';
      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          errorText = error.response.data.detail;
        } else if (typeof error.response.data.detail === 'object') {
          // 如果是对象，尝试获取其中的消息
          errorText = error.response.data.detail.msg || JSON.stringify(error.response.data.detail);
        }
      } else if (error.message) {
        errorText = error.message;
      }
      setMessage({ type: 'error', text: errorText });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <PageContainer>
        <PageHeader>
          <PageTitle
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            🎭 数字人中心
          </PageTitle>
          <PageDesc
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            管理您的数字人形象，选择公模或创建专属克隆人
          </PageDesc>
        </PageHeader>

        <TabContainer>
          <Tab $active={activeTab === 'public'} onClick={() => setActiveTab('public')}>
            🏢 公模库
          </Tab>
          <Tab $active={activeTab === 'clone'} onClick={() => setActiveTab('clone')}>
            ✨ 克隆形象
          </Tab>
          <Tab $active={activeTab === 'myclones'} onClick={() => setActiveTab('myclones')}>
            📁 我的形象
          </Tab>
          <Tab $active={activeTab === 'tasks'} onClick={() => setActiveTab('tasks')}>
            📋 任务管理
          </Tab>
          <Tab $active={activeTab === 'create'} onClick={() => setActiveTab('create')}>
            🎬 创作视频
          </Tab>
          <Tab $active={activeTab === 'preview'} onClick={() => setActiveTab('preview')}>
            🎥 成片预览
          </Tab>
        </TabContainer>

        {(activeTab === 'public' || activeTab === 'myclones') && (
          <PlatformSelector>
            <PlatformLabel>选择数字人版本：</PlatformLabel>
            <PlatformButton 
              $active={selectedPlatform === 'Pro'} 
              onClick={() => setSelectedPlatform('Pro')}
            >
              Pro商业版
            </PlatformButton>
            <PlatformButton 
              $active={selectedPlatform === 'VPro'} 
              onClick={() => setSelectedPlatform('VPro')}
            >
              VPro高清版
            </PlatformButton>
          </PlatformSelector>
        )}

        <AnimatePresence mode="wait">
          {activeTab === 'public' && (
            <motion.div
              key="public"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              {loading ? (
                <Card>加载中...</Card>
              ) : publicModels.length > 0 ? (
                <Grid>
                  {publicModels.map((model) => (
                    <ModelCard
                      key={model.id}
                      selected={selectedModel?.id === model.id}
                      onClick={() => { setSelectedModel(model); setModelSource('public'); }}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <ModelImage>
                        {model.video_url || model.video_oss_url ? (
                          (() => {
                            const videoUrl = model.video_url || model.video_oss_url;
                            let videoSnapshot = null;
                            if (videoUrl && videoUrl.includes('.mp4')) {
                              const separator = videoUrl.includes('?') ? '&' : '?';
                              videoSnapshot = `${videoUrl}${separator}x-oss-process=video/snapshot,t_1000,f_jpg`;
                            }
                            return (
                              <div 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openVideoPlayer(videoUrl, model.name);
                                }}
                                style={{ 
                                  display: 'block', 
                                  width: '100%', 
                                  height: '100%',
                                  cursor: 'pointer',
                                  position: 'relative',
                                  borderRadius: '50%',
                                  overflow: 'hidden'
                                }}
                              >
                                {videoSnapshot ? (
                                  <img 
                                    src={videoSnapshot} 
                                    alt={model.name}
                                    style={{ 
                                      width: '100%', 
                                      height: '100%', 
                                      objectFit: 'cover',
                                      borderRadius: '8px'
                                    }}
                                    onError={(e) => {
                                      e.target.onerror = null;
                                      e.target.style.display = 'none';
                                      if (e.target.parentElement) {
                                        e.target.parentElement.innerHTML = `
                                          <div style="display:flex;align-items:center;justify-content:center;height:100%;background:linear-gradient(135deg, #00f0ff, #4facfe);border-radius:8px;">
                                            <span style="font-size:28px;">🎬</span>
                                          </div>
                                        `;
                                      }
                                    }}
                                  />
                                ) : (
                                  <div style={{
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background: 'linear-gradient(135deg, #00f0ff, #4facfe)',
                                    borderRadius: '8px'
                                  }}>
                                    <span style={{ fontSize: '28px' }}>🎬</span>
                                  </div>
                                )}
                                <div style={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  width: '100%',
                                  height: '100%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  background: 'rgba(0, 0, 0, 0)',
                                  transition: 'background 0.3s ease',
                                  borderRadius: '8px'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0, 0, 0, 0.3)'}
                                onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(0, 0, 0, 0)'}
                                >
                                  <div style={{
                                    background: 'rgba(0, 0, 0, 0.7)',
                                    borderRadius: '50%',
                                    width: '36px',
                                    height: '36px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    opacity: 0,
                                    transition: 'opacity 0.3s ease'
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                                  onMouseLeave={(e) => e.currentTarget.style.opacity = '0'}
                                  >
                                    <span style={{ color: 'white', fontSize: '16px' }}>▶</span>
                                  </div>
                                </div>
                              </div>
                            );
                          })()
                        ) : (
                          model.sex === 1 ? '👨' : '👩'
                        )}
                      </ModelImage>
                      <ModelInfo>
                        <h4>{model.name}</h4>
                        <p>{model.platform_name || '官方公模'}</p>
                        <p style={{ fontSize: '11px', color: model.platform_code === 'Pro' ? '#00f0ff' : '#ff6b6b', marginTop: '4px' }}>
                          {model.platform_code === 'Pro' ? 'Pro商业版' : 'VPro高清版'}
                        </p>
                      </ModelInfo>
                    </ModelCard>
                  ))}
                </Grid>
              ) : (
                <Card>
                  <EmptyState>
                    <h3>暂无公模</h3>
                    <p>请先在配置中心配置API凭证</p>
                  </EmptyState>
                </Card>
              )}
            </motion.div>
          )}

          {activeTab === 'clone' && (
            <motion.div
              key="clone"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Card>
                <h3 style={{ marginBottom: '20px', color: 'var(--primary)' }}>
                  📤 AI数字人形象克隆
                </h3>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px' }}>
                  <div>
                    <FormGroup>
                      <Label>形象名称 *</Label>
                      <Input
                        type="text"
                        placeholder="数字人名称"
                        value={cloneForm.name}
                        onChange={(e) => setCloneForm({...cloneForm, name: e.target.value})}
                      />
                    </FormGroup>
                    
                    <FormGroup>
                      <Label>克隆模式</Label>
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <Button
                          type="button"
                          style={{ 
                            background: 'linear-gradient(135deg, #00f2fe 0%, #4facfe 100%)',
                            pointerEvents: 'none'
                          }}
                        >
                          PRO商业版
                        </Button>
                      </div>
                    </FormGroup>
                    
                    <FormGroup>
                      <Label>性别</Label>
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <Button
                          type="button"
                          style={{ 
                            background: cloneForm.sex === 1 ? 'linear-gradient(135deg, #00f2fe 0%, #4facfe 100%)' : 'rgba(0, 0, 0, 0.3)',
                            border: '1px solid var(--border-color)'
                          }}
                          onClick={() => setCloneForm({...cloneForm, sex: 1})}
                        >
                          男
                        </Button>
                        <Button
                          type="button"
                          style={{ 
                            background: cloneForm.sex === 2 ? 'linear-gradient(135deg, #00f2fe 0%, #4facfe 100%)' : 'rgba(0, 0, 0, 0.3)',
                            border: '1px solid var(--border-color)'
                          }}
                          onClick={() => setCloneForm({...cloneForm, sex: 2})}
                        >
                          女
                        </Button>
                      </div>
                    </FormGroup>
                    
                    <FormGroup>
                      <Label>人像视频</Label>
                      <Input
                        type="text"
                        placeholder="https://your-bucket.oss-cn-shenzhen.aliyuncs.com/video.mp4"
                        value={cloneForm.video_oss_url}
                        onChange={(e) => setCloneForm({...cloneForm, video_oss_url: e.target.value})}
                      />
                      <p style={{ fontSize: '12px', color: '#a0a0b0', marginTop: '8px' }}>
                        请上传包含正面人脸的视频到您的OSS存储，获取视频地址后填入
                      </p>
                    </FormGroup>
                    
                    <FormGroup>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <input
                          type="checkbox"
                          id="agree"
                          checked={cloneForm.agree}
                          onChange={(e) => setCloneForm({...cloneForm, agree: e.target.checked})}
                        />
                        <label htmlFor="agree" style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                          我已知阅并同意 <span style={{ color: '#00f2fe' }}>《使用协议》</span>
                        </label>
                      </div>
                    </FormGroup>
                    
                    <Button
                      type="button"
                      onClick={handleCloneSubmit}
                      disabled={loading || !cloneForm.video_oss_url || !cloneForm.name || !cloneForm.agree}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {loading ? '提交中...' : '克隆形象(消耗100积分)'}
                    </Button>
                  </div>
                  
                  <div>
                    <h4 style={{ marginBottom: '15px', color: 'var(--text-primary)' }}>示例视频</h4>
                    <div style={{ 
                      background: 'rgba(0, 0, 0, 0.3)', 
                      border: '1px solid var(--border-color)',
                      borderRadius: '8px',
                      padding: '20px',
                      marginBottom: '20px'
                    }}>
                      <div style={{ 
                        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
                        height: '200px',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '48px', marginBottom: '10px' }}>🎬</div>
                          <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
                            示例视频预览
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <h4 style={{ marginBottom: '10px', color: 'var(--text-primary)' }}>素材要求</h4>
                    <div style={{ 
                      background: 'rgba(0, 0, 0, 0.3)', 
                      border: '1px solid var(--border-color)',
                      borderRadius: '8px',
                      padding: '15px'
                    }}>
                      <div style={{ marginBottom: '10px' }}>
                        <h5 style={{ marginBottom: '8px', color: 'var(--text-secondary)' }}>✅ 推荐要求</h5>
                        <ul style={{ 
                          listStyle: 'none', 
                          padding: 0, 
                          margin: 0,
                          fontSize: '12px',
                          color: 'var(--text-secondary)'
                        }}>
                          <li style={{ marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ color: '#00ff88' }}>✓</span> 提交30-120秒的视频素材
                          </li>
                          <li style={{ marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ color: '#00ff88' }}>✓</span> 人脸占比不超过画面1/2
                          </li>
                          <li style={{ marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ color: '#00ff88' }}>✓</span> 可静坐、比划、简单手势等
                          </li>
                          <li style={{ marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ color: '#00ff88' }}>✓</span> 尽量满足光线照明均匀
                          </li>
                        </ul>
                      </div>
                      <div>
                        <h5 style={{ marginBottom: '8px', color: 'var(--text-secondary)' }}>❌ 避免情况</h5>
                        <ul style={{ 
                          listStyle: 'none', 
                          padding: 0, 
                          margin: 0,
                          fontSize: '12px',
                          color: 'var(--text-secondary)'
                        }}>
                          <li style={{ marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ color: '#ff3366' }}>✕</span> 过长素材会导致合成失败或质量差
                          </li>
                          <li style={{ marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ color: '#ff3366' }}>✕</span> 人脸占比太大或太小
                          </li>
                          <li style={{ marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ color: '#ff3366' }}>✕</span> 明显的阴影和曝光过度，甚至画面模糊
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
                
                {message && (
                  <ResultBox error={message.type === 'error'}>
                    <pre>{message.text}</pre>
                  </ResultBox>
                )}
              </Card>
            </motion.div>
          )}

          {activeTab === 'myclones' && (
            <motion.div
              key="myclones"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Card>
                <h3 style={{ marginBottom: '20px', color: 'var(--primary)' }}>
                  📁 我的数字人形象
                </h3>
                {loading ? (
                  <div style={{ textAlign: 'center', padding: '40px' }}>加载中...</div>
                ) : myClones.length > 0 ? (
                  <Grid>
                    {myClones.map((model) => (
                      <ModelCard
                        key={model.id}
                        selected={selectedModel?.id === model.id}
                        onClick={() => { setSelectedModel(model); setModelSource('private'); }}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <ModelImage>
                          {model.video_oss_url ? (
                            (() => {
                              const videoUrl = model.video_oss_url;
                              let videoSnapshot = null;
                              if (videoUrl && videoUrl.includes('.mp4')) {
                                const separator = videoUrl.includes('?') ? '&' : '?';
                                videoSnapshot = `${videoUrl}${separator}x-oss-process=video/snapshot,t_1000,f_jpg`;
                              }
                              return (
                                <div 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openVideoPlayer(videoUrl, model.name);
                                  }}
                                  style={{ 
                                    display: 'block', 
                                    width: '100%', 
                                    height: '100%',
                                    cursor: 'pointer',
                                    position: 'relative',
                                    borderRadius: '50%',
                                    overflow: 'hidden'
                                  }}
                                >
                                  {videoSnapshot ? (
                                    <img 
                                      src={videoSnapshot} 
                                      alt={model.name}
                                      style={{ 
                                        width: '100%', 
                                        height: '100%', 
                                        objectFit: 'cover',
                                        borderRadius: '8px'
                                      }}
                                      onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.style.display = 'none';
                                        if (e.target.parentElement) {
                                          e.target.parentElement.innerHTML = `
                                            <div style="display:flex;align-items:center;justify-content:center;height:100%;background:linear-gradient(135deg, #00f0ff, #4facfe);border-radius:8px;">
                                              <span style="font-size:28px;">🎬</span>
                                            </div>
                                          `;
                                        }
                                      }}
                                    />
                                  ) : (
                                    <div style={{
                                      width: '100%',
                                      height: '100%',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      background: 'linear-gradient(135deg, #00f0ff, #4facfe)',
                                      borderRadius: '8px'
                                    }}>
                                      <span style={{ fontSize: '28px' }}>🎬</span>
                                    </div>
                                  )}
                                  <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background: 'rgba(0, 0, 0, 0)',
                                    transition: 'background 0.3s ease',
                                    borderRadius: '8px'
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0, 0, 0, 0.3)'}
                                  onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(0, 0, 0, 0)'}
                                  >
                                    <div style={{
                                      background: 'rgba(0, 0, 0, 0.7)',
                                      borderRadius: '50%',
                                      width: '36px',
                                      height: '36px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      opacity: 0,
                                      transition: 'opacity 0.3s ease'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                                    onMouseLeave={(e) => e.currentTarget.style.opacity = '0'}
                                    >
                                      <span style={{ color: 'white', fontSize: '16px' }}>▶</span>
                                    </div>
                                  </div>
                                </div>
                              );
                            })()
                          ) : (
                            model.sex === 1 ? '👨' : '👩'
                          )}
                        </ModelImage>
                        <ModelInfo>
                          <h4>{model.name}</h4>
                          <p>{model.platform_name || '我的克隆'}</p>
                          <p style={{ fontSize: '11px', color: model.platform_code === 'Pro' ? '#00f0ff' : '#ff6b6b', marginTop: '4px' }}>
                            {model.platform_code === 'Pro' ? 'Pro商业版' : 'VPro高清版'}
                          </p>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigateToVideo(model);
                            }}
                            style={{
                              marginTop: '10px',
                              width: '100%',
                              padding: '8px 12px',
                              background: 'linear-gradient(135deg, #00f0ff 0%, #0099ff 100%)',
                              border: 'none',
                              borderRadius: '6px',
                              color: '#fff',
                              fontSize: '13px',
                              cursor: 'pointer',
                              fontWeight: '500'
                            }}
                          >
                            🎬 立即创作
                          </button>
                        </ModelInfo>
                      </ModelCard>
                    ))}
                  </Grid>
                ) : (
                  <EmptyState>
                    <h3>暂无克隆形象</h3>
                    <p>请先在"克隆形象"标签页创建您的数字人</p>
                  </EmptyState>
                )}
              </Card>
            </motion.div>
          )}

          {activeTab === 'tasks' && (
            <motion.div
              key="tasks"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Card>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <h3 style={{ color: 'var(--primary)' }}>
                    📋 任务管理
                  </h3>
                  <Button
                    type="button"
                    onClick={fetchTasks}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    🔄 刷新
                  </Button>
                </div>
                
                {loading ? (
                  <div style={{ textAlign: 'center', padding: '40px' }}>加载中...</div>
                ) : tasks.length > 0 ? (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ 
                      width: '100%', 
                      borderCollapse: 'collapse',
                      fontSize: '14px'
                    }}>
                      <thead>
                        <tr style={{ 
                          background: 'rgba(0, 240, 255, 0.1)',
                          borderBottom: '1px solid var(--border-color)'
                        }}>
                          <th style={{ padding: '12px', textAlign: 'left', color: 'var(--text-primary)' }}>ID</th>
                          <th style={{ padding: '12px', textAlign: 'left', color: 'var(--text-primary)' }}>任务名</th>
                          <th style={{ padding: '12px', textAlign: 'left', color: 'var(--text-primary)' }}>生成通道</th>
                          <th style={{ padding: '12px', textAlign: 'left', color: 'var(--text-primary)' }}>语音</th>
                          <th style={{ padding: '12px', textAlign: 'left', color: 'var(--text-primary)' }}>象形链接</th>
                          <th style={{ padding: '12px', textAlign: 'left', color: 'var(--text-primary)' }}>声音链接</th>
                          <th style={{ padding: '12px', textAlign: 'left', color: 'var(--text-primary)' }}>合成后链接</th>
                          <th style={{ padding: '12px', textAlign: 'left', color: 'var(--text-primary)' }}>成片</th>
                          <th style={{ padding: '12px', textAlign: 'left', color: 'var(--text-primary)' }}>状态</th>
                          <th style={{ padding: '12px', textAlign: 'left', color: 'var(--text-primary)' }}>提交时间</th>
                          <th style={{ padding: '12px', textAlign: 'left', color: 'var(--text-primary)' }}>创建时间</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tasks.map((task) => (
                          <tr 
                            key={task.id} 
                            style={{
                              borderBottom: '1px solid var(--border-color)',
                              '&:hover': {
                                background: 'rgba(0, 240, 255, 0.05)'
                              }
                            }}
                          >
                            <td style={{ padding: '12px', color: 'var(--text-secondary)' }}>{task.id}</td>
                            <td style={{ padding: '12px', color: 'var(--text-primary)' }}>{task.task_name}</td>
                            <td style={{ padding: '12px', color: 'var(--text-secondary)' }}>
                              <span style={{ 
                                background: task.generate_channel === 'pro商业版' ? 'rgba(0, 240, 255, 0.15)' : 'rgba(255, 107, 107, 0.15)',
                                color: task.generate_channel === 'pro商业版' ? '#00f2fe' : '#ff6b6b',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '12px'
                              }}>
                                {task.generate_channel}
                              </span>
                            </td>
                            <td style={{ padding: '12px', color: 'var(--text-secondary)' }}>{task.voice}</td>
                            <td style={{ padding: '12px', color: 'var(--text-secondary)' }}>
                              {task.model_video_url ? (
                                <a 
                                  href={task.model_video_url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  style={{ color: '#00f0ff', textDecoration: 'none', fontSize: '11px' }}
                                  title={task.model_name}
                                >
                                  {task.model_name}
                                </a>
                              ) : (
                                <span style={{ color: 'var(--text-secondary)', fontSize: '11px' }}>无链接</span>
                              )}
                            </td>
                            <td style={{ padding: '12px', color: 'var(--text-secondary)' }}>
                              {task.voice_url ? (
                                <a 
                                  href={task.voice_url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  style={{ color: '#00f0ff', textDecoration: 'none', fontSize: '11px' }}
                                >
                                  声音链接
                                </a>
                              ) : (
                                <span style={{ color: 'var(--text-secondary)', fontSize: '11px' }}>无链接</span>
                              )}
                            </td>
                            <td style={{ padding: '12px', color: 'var(--text-secondary)' }}>
                              {task.video_url ? (
                                <a 
                                  href={task.video_url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  style={{ color: '#00f0ff', textDecoration: 'none', fontSize: '11px' }}
                                >
                                  视频链接
                                </a>
                              ) : (
                                <span style={{ color: 'var(--text-secondary)', fontSize: '11px' }}>无链接</span>
                              )}
                            </td>
                            <td style={{ padding: '12px' }}>
                              <div style={{ 
                                width: '60px', 
                                height: '40px',
                                borderRadius: '4px',
                                overflow: 'hidden',
                                border: '1px solid var(--border-color)'
                              }}>
                                <img 
                                  src={task.thumbnail} 
                                  alt="成片预览" 
                                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                              </div>
                            </td>
                            <td style={{ padding: '12px' }}>
                              <StatusBadge status={task.status}>
                                {task.status_text}
                              </StatusBadge>
                            </td>
                            <td style={{ padding: '12px', color: 'var(--text-secondary)', fontSize: '11px' }}>{task.submit_time}</td>
                            <td style={{ padding: '12px', color: 'var(--text-secondary)', fontSize: '11px' }}>{task.created_time}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <EmptyState>
                    <h3>暂无任务</h3>
                    <p>请先创建数字人视频</p>
                  </EmptyState>
                )}
              </Card>
            </motion.div>
          )}

          {activeTab === 'create' && (
            <motion.div
              key="create"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Card>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px' }}>
                  <div>
                    <h3 style={{ marginBottom: '20px', color: 'var(--primary)' }}>
                      🎬 AI数字人合成
                    </h3>
                    
                    <FormGroup>
                      <Label>选择数字人形象</Label>
                      {selectedModel ? (
                        <div style={{ 
                          padding: '16px', 
                          background: 'rgba(0, 240, 255, 0.1)', 
                          border: '1px solid var(--primary)',
                          borderRadius: '8px',
                          marginBottom: '10px'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            {selectedModel.video_oss_url ? (
                              <div style={{ 
                                width: '40px', 
                                height: '40px', 
                                borderRadius: '6px',
                                overflow: 'hidden',
                                flexShrink: 0,
                                cursor: 'pointer'
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                openVideoPlayer(selectedModel.video_oss_url, selectedModel.name);
                              }}
                              >
                                {(() => {
                                  const videoUrl = selectedModel.video_oss_url;
                                  let videoSnapshot = null;
                                  if (videoUrl && videoUrl.includes('.mp4')) {
                                    const separator = videoUrl.includes('?') ? '&' : '?';
                                    videoSnapshot = `${videoUrl}${separator}x-oss-process=video/snapshot,t_1000,f_jpg`;
                                  }
                                  return videoSnapshot ? (
                                    <img 
                                      src={videoSnapshot} 
                                      alt={selectedModel.name}
                                      style={{ 
                                        width: '100%', 
                                        height: '100%', 
                                        objectFit: 'cover'
                                      }}
                                      onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.style.display = 'none';
                                        if (e.target.parentElement) {
                                          e.target.parentElement.innerHTML = selectedModel.sex === 1 ? '👨' : '👩';
                                        }
                                      }}
                                    />
                                  ) : (
                                    <div style={{
                                      width: '100%',
                                      height: '100%',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      background: 'linear-gradient(135deg, #00f0ff, #4facfe)',
                                      fontSize: '18px'
                                    }}>
                                      🎬
                                    </div>
                                  );
                                })()}
                              </div>
                            ) : (
                              <div style={{ 
                                width: '40px', 
                                height: '40px', 
                                background: 'rgba(0, 240, 255, 0.2)',
                                borderRadius: '6px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '18px'
                              }}>
                                {selectedModel.sex === 1 ? '👨' : '👩'}
                              </div>
                            )}
                            <div>
                              <strong>{selectedModel.name}</strong>
                              <div style={{ fontSize: '12px', color: '#a0a0b0', marginTop: '2px' }}>
                                {selectedModel.platform_name || '我的克隆'}
                              </div>
                            </div>
                          </div>
                          <span style={{ marginLeft: '10px', color: '#00ff88', fontSize: '12px' }}>✓ 已选择</span>
                          <button
                            onClick={() => { setSelectedModel(null); setModelSource(null); }}
                            style={{
                              marginLeft: '10px',
                              background: 'transparent',
                              border: '1px solid #ff6b6b',
                              color: '#ff6b6b',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              cursor: 'pointer'
                            }}
                          >
                            更换
                          </button>
                        </div>
                      ) : (
                        <div style={{ 
                          padding: '16px', 
                          background: 'rgba(0, 0, 0, 0.3)', 
                          border: '1px solid var(--border-color)',
                          borderRadius: '8px',
                          marginBottom: '10px'
                        }}>
                          <p style={{ color: '#a0a0b0', marginBottom: '10px' }}>请选择您的专属克隆形象：</p>
                          {myClones.length > 0 ? (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', maxHeight: '200px', overflowY: 'auto' }}>
                              {myClones.map((model) => (
                                <div
                                  key={model.id}
                                  onClick={() => { setSelectedModel(model); setModelSource('private'); }}
                                  style={{
                                    cursor: 'pointer',
                                    border: selectedModel?.id === model.id ? '2px solid #00f0ff' : '1px solid var(--border-color)',
                                    borderRadius: '8px',
                                    padding: '8px',
                                    background: selectedModel?.id === model.id ? 'rgba(0, 240, 255, 0.1)' : 'rgba(0, 0, 0, 0.3)',
                                    transition: 'all 0.3s ease'
                                  }}
                                >
                                  {model.video_oss_url ? (
                                    <div style={{ width: '100%', aspectRatio: '1', borderRadius: '4px', overflow: 'hidden' }}>
                                      {(() => {
                                        const videoUrl = model.video_oss_url;
                                        let videoSnapshot = null;
                                        if (videoUrl && videoUrl.includes('.mp4')) {
                                          const separator = videoUrl.includes('?') ? '&' : '?';
                                          videoSnapshot = `${videoUrl}${separator}x-oss-process=video/snapshot,t_1000,f_jpg`;
                                        }
                                        return videoSnapshot ? (
                                          <img 
                                            src={videoSnapshot} 
                                            alt={model.name}
                                            style={{ 
                                              width: '100%', 
                                              height: '100%', 
                                              objectFit: 'cover'
                                            }}
                                            onError={(e) => {
                                              e.target.onerror = null;
                                              e.target.style.display = 'none';
                                              if (e.target.parentElement) {
                                                e.target.parentElement.innerHTML = model.sex === 1 ? '👨' : '👩';
                                              }
                                            }}
                                          />
                                        ) : (
                                          <div style={{
                                            width: '100%',
                                            height: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            background: 'linear-gradient(135deg, #00f0ff, #4facfe)'
                                          }}>
                                            🎬
                                          </div>
                                        );
                                      })()}
                                    </div>
                                  ) : (
                                    <div style={{ 
                                      width: '100%', 
                                      aspectRatio: '1', 
                                      borderRadius: '4px',
                                      background: 'linear-gradient(135deg, #00f0ff, #4facfe)',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: '24px'
                                    }}>
                                      {model.sex === 1 ? '👨' : '👩'}
                                    </div>
                                  )}
                                  <p style={{ fontSize: '12px', color: '#a0a0b0', marginTop: '5px', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {model.name}
                                  </p>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p style={{ color: '#ff6b6b', fontSize: '14px' }}>暂无克隆形象，请先在"克隆形象"标签页创建</p>
                          )}
                        </div>
                      )}
                    </FormGroup>
                    
                    <FormGroup>
                      <Label>任务名称</Label>
                      <Input
                        type="text"
                        placeholder="任务名，随便写一个吧"
                        value={createForm.task_name || ''}
                        onChange={(e) => setCreateForm({...createForm, task_name: e.target.value})}
                      />
                    </FormGroup>
                    
                    <FormGroup>
                      <Label>语音来源</Label>
                      <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                          <input
                            type="radio"
                            name="voice_source"
                            value="text"
                            checked={createForm.voice_source === 'text' || !createForm.voice_source}
                            onChange={(e) => setCreateForm({...createForm, voice_source: e.target.value})}
                          />
                          <span style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>文本转语音</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                          <input
                            type="radio"
                            name="voice_source"
                            value="upload"
                            checked={createForm.voice_source === 'upload'}
                            onChange={(e) => setCreateForm({...createForm, voice_source: e.target.value})}
                          />
                          <span style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>上传</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                          <input
                            type="radio"
                            name="voice_source"
                            value="history"
                            checked={createForm.voice_source === 'history'}
                            onChange={(e) => setCreateForm({...createForm, voice_source: e.target.value})}
                          />
                          <span style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>历史合成</span>
                        </label>
                      </div>
                      
                      {createForm.voice_source === 'upload' && (
                        <div style={{ marginTop: '10px' }}>
                          <input
                            type="file"
                            accept="audio/*"
                            onChange={handleVoiceUpload}
                            disabled={uploading}
                            style={{
                              padding: '10px',
                              marginBottom: '10px',
                              width: '100%'
                            }}
                          />
                          {uploading && (
                            <div style={{ 
                              background: 'rgba(0, 240, 255, 0.1)', 
                              border: '1px solid var(--primary)',
                              borderRadius: '4px',
                              padding: '10px',
                              marginTop: '10px'
                            }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                                <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>上传中...</span>
                                <span style={{ color: 'var(--primary)', fontSize: '12px' }}>{uploadProgress}%</span>
                              </div>
                              <div style={{ 
                                height: '4px', 
                                background: 'rgba(0, 240, 255, 0.2)', 
                                borderRadius: '2px',
                                overflow: 'hidden'
                              }}>
                                <div style={{ 
                                  height: '100%', 
                                  width: `${uploadProgress}%`,
                                  background: 'linear-gradient(90deg, #00f2fe 0%, #4facfe 100%)',
                                  transition: 'width 0.3s ease'
                                }} />
                              </div>
                            </div>
                          )}
                          {createForm.audio_oss_url && (
                            <div style={{ 
                              background: 'rgba(0, 255, 136, 0.1)', 
                              border: '1px solid rgba(0, 255, 136, 0.3)',
                              borderRadius: '4px',
                              padding: '10px',
                              marginTop: '10px',
                              fontSize: '12px',
                              color: '#00ff88'
                            }}>
                              ✅ 声音文件已上传
                            </div>
                          )}
                        </div>
                      )}
                      
                      {createForm.voice_source === 'history' && (
                        <div style={{ marginTop: '10px' }}>
                          {voiceHistory.length > 0 ? (
                            <div style={{ 
                              maxHeight: '200px', 
                              overflowY: 'auto',
                              border: '1px solid var(--border-color)',
                              borderRadius: '8px',
                              padding: '10px'
                            }}>
                              {voiceHistory.map((voice) => (
                                <div 
                                  key={voice.id}
                                  style={{
                                    padding: '10px',
                                    borderBottom: '1px solid var(--border-color)',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                      background: 'rgba(0, 240, 255, 0.1)'
                                    }
                                  }}
                                  onClick={() => selectHistoryVoice(voice)}
                                >
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ color: 'var(--text-primary)', fontSize: '14px' }}>{voice.filename}</span>
                                    <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>
                                      {new Date(voice.created_at).toLocaleString()}
                                    </span>
                                  </div>
                                  <div style={{ 
                                    marginTop: '5px', 
                                    color: 'var(--text-secondary)', 
                                    fontSize: '12px'
                                  }}>
                                    {Math.round(voice.size / 1024)}KB
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div style={{ 
                              padding: '20px', 
                              background: 'rgba(0, 0, 0, 0.3)',
                              border: '1px solid var(--border-color)',
                              borderRadius: '8px',
                              textAlign: 'center',
                              color: 'var(--text-secondary)'
                            }}>
                              暂无历史声音记录
                            </div>
                          )}
                        </div>
                      )}
                    </FormGroup>
                    
                    {createForm.voice_source === 'text' && (
                      <FormGroup>
                        <Label>文案内容 *</Label>
                        <TextArea
                          placeholder="输入需要合成的文字，一行一个语音，多行的话，即为批量合成多个视频"
                          value={createForm.text}
                          onChange={(e) => setCreateForm({...createForm, text: e.target.value})}
                        />
                        <p style={{ fontSize: '12px', color: '#a0a0b0', marginTop: '8px' }}>
                          每行不超过500字；一行一个语音，多行则为此账号合成多个视频
                        </p>
                      </FormGroup>
                    )}
                    
                    {createForm.voice_source === 'text' && (
                      <>
                        <FormGroup>
                          <Label>音色选择</Label>
                          <Select
                            value={createForm.voice_id || ''}
                            onChange={(e) => setCreateForm({...createForm, voice_id: parseInt(e.target.value) || null})}
                          >
                            <option value="">不选择</option>
                            {voiceList.map(voice => (
                              <option key={voice.voice_id} value={voice.voice_id}>
                                {voice.voice_name} ({voice.sex === 1 ? '男' : '女'})
                              </option>
                            ))}
                          </Select>
                        </FormGroup>
                        
                        <FormGroup>
                          <Label>字幕分类</Label>
                          <Select
                            value={createForm.subtitle_type || ''}
                            onChange={(e) => setCreateForm({...createForm, subtitle_type: e.target.value})}
                          >
                            <option value="">请选择素材分类</option>
                            <option value="1">科技</option>
                            <option value="2">教育</option>
                            <option value="3">娱乐</option>
                            <option value="4">商业</option>
                          </Select>
                        </FormGroup>
                      </>
                    )}
                    
                    <Button
                      onClick={handleCreateVideo}
                      disabled={loading || !selectedModel || (createForm.voice_source === 'text' && !createForm.text) || ((createForm.voice_source === 'upload' || createForm.voice_source === 'history') && !createForm.audio_oss_url)}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {loading ? '生成中...' : '合成视频 (10积分/秒)'}
                    </Button>
                  </div>
                  
                  <div>
                    <h4 style={{ marginBottom: '15px', color: 'var(--text-primary)' }}>数字人形象</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
                      {publicModels.slice(0, 3).map((model) => (
                        <div 
                          key={model.id}
                          style={{
                            background: 'rgba(0, 0, 0, 0.3)',
                            border: selectedModel?.id === model.id ? '1px solid var(--primary)' : '1px solid var(--border-color)',
                            borderRadius: '8px',
                            padding: '10px',
                            textAlign: 'center',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease'
                          }}
                          onClick={() => { setSelectedModel(model); setModelSource('public'); }}
                        >
                          <div style={{
                            width: '80px',
                            height: '80px',
                            margin: '0 auto 8px',
                            background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '24px'
                          }}>
                            {model.sex === 1 ? '👨' : '👩'}
                          </div>
                          <p style={{ fontSize: '12px', color: 'var(--text-primary)', margin: 0 }}>
                            {model.name}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                
                {message && (
                  <ResultBox error={message.type === 'error'}>
                    <pre>{message.text}</pre>
                  </ResultBox>
                )}
              </Card>
            </motion.div>
          )}
          {activeTab === 'preview' && (
            <motion.div
              key="preview"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Card>
                <h3 style={{ marginBottom: '20px', color: 'var(--primary)' }}>
                  🎥 成片预览
                </h3>
                
                {loading ? (
                  <div style={{ textAlign: 'center', padding: '40px' }}>加载中...</div>
                ) : videos.length > 0 ? (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '20px', marginBottom: '30px' }}>
                      {videos.map((video) => (
                        <div 
                          key={video.id}
                          style={{
                            background: 'rgba(0, 0, 0, 0.3)',
                            border: '1px solid var(--border-color)',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            transition: 'all 0.3s ease',
                            cursor: 'pointer'
                          }}
                          onClick={() => openVideoPreview(video)}
                        >
                          <div style={{ position: 'relative' }}>
                            <div style={{
                              width: '100%',
                              height: '120px',
                              overflow: 'hidden'
                            }}>
                              <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                                {video.video_url || video.thumbnail ? (
                                  <a 
                                    href={video.video_url || video.thumbnail} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    style={{ display: 'block', width: '100%', height: '100%' }}
                                  >
                                    <img 
                                      src={video.video_url || video.thumbnail} 
                                      alt={video.title} 
                                      onLoad={(e) => {
                                        e.target.style.opacity = '1';
                                        if (e.target.nextElementSibling) {
                                          e.target.nextElementSibling.style.display = 'none';
                                        }
                                      }}
                                      onError={(e) => {
                                        e.target.style.opacity = '0';
                                        if (e.target.nextElementSibling) {
                                          e.target.nextElementSibling.style.display = 'flex';
                                        }
                                      }}
                                      style={{ 
                                        width: '100%', 
                                        height: '100%', 
                                        objectFit: 'cover',
                                        opacity: 0,
                                        transition: 'opacity 0.3s ease'
                                      }}
                                    />
                                  </a>
                                ) : (
                                  <img 
                                    src={video.thumbnail} 
                                    alt={video.title} 
                                    onLoad={(e) => {
                                      e.target.style.opacity = '1';
                                      if (e.target.nextElementSibling) {
                                        e.target.nextElementSibling.style.display = 'none';
                                      }
                                    }}
                                    onError={(e) => {
                                      e.target.style.opacity = '0';
                                      if (e.target.nextElementSibling) {
                                        e.target.nextElementSibling.style.display = 'flex';
                                      }
                                    }}
                                    style={{ 
                                      width: '100%', 
                                      height: '100%', 
                                      objectFit: 'cover',
                                      opacity: 0,
                                      transition: 'opacity 0.3s ease'
                                    }}
                                  />
                                )}
                                <div style={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  width: '100%',
                                  height: '100%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  background: 'rgba(0, 0, 0, 0.1)'
                                }}>
                                  <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>加载中...</span>
                                </div>
                              </div>
                            </div>
                            <div style={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              transform: 'translate(-50%, -50%)',
                              background: 'rgba(0, 0, 0, 0.6)',
                              borderRadius: '50%',
                              width: '40px',
                              height: '40px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <span style={{ color: 'white', fontSize: '16px' }}>▶</span>
                            </div>
                            <div style={{
                              position: 'absolute',
                              bottom: '8px',
                              right: '8px',
                              background: 'rgba(0, 0, 0, 0.6)',
                              color: 'white',
                              fontSize: '10px',
                              padding: '2px 6px',
                              borderRadius: '4px'
                            }}>
                              {video.duration}
                            </div>
                          </div>
                          <div style={{ padding: '10px' }}>
                            <p style={{ fontSize: '12px', color: 'var(--text-primary)', margin: '0 0 4px 0' }}>
                              {video.title}
                            </p>
                            <p style={{ fontSize: '10px', color: 'var(--text-secondary)', margin: 0 }}>
                              数字人: {video.digital_human}
                            </p>
                            {video.video_url && (
                              <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                                <a 
                                  href={video.video_url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  style={{
                                    flex: 1,
                                    padding: '6px 12px',
                                    background: 'linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%)',
                                    border: 'none',
                                    borderRadius: '4px',
                                    color: 'white',
                                    fontSize: '11px',
                                    textAlign: 'center',
                                    cursor: 'pointer',
                                    textDecoration: 'none'
                                  }}
                                >
                                  ▶ 播放
                                </a>
                                <a 
                                  href={video.video_url} 
                                  download={video.title + '.mp4'}
                                  rel="noopener noreferrer"
                                  style={{
                                    flex: 1,
                                    padding: '6px 12px',
                                    background: 'rgba(0, 240, 255, 0.2)',
                                    border: '1px solid var(--primary)',
                                    borderRadius: '4px',
                                    color: 'var(--primary)',
                                    fontSize: '11px',
                                    textAlign: 'center',
                                    cursor: 'pointer',
                                    textDecoration: 'none'
                                  }}
                                  onClick={(e) => {
                                    console.log('[下载视频]', video.video_url);
                                  }}
                                >
                                  ↓ 下载
                                </a>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px' }}>
                      <Button
                        type="button"
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        style={{ 
                          background: 'rgba(0, 0, 0, 0.3)',
                          border: '1px solid var(--border-color)'
                        }}
                      >
                        ← 上一页
                      </Button>
                      <div style={{ display: 'flex', gap: '5px' }}>
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                          <Button
                            key={page}
                            type="button"
                            onClick={() => setCurrentPage(page)}
                            style={{ 
                              background: currentPage === page ? 'linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%)' : 'rgba(0, 0, 0, 0.3)',
                              border: '1px solid var(--border-color)',
                              minWidth: '32px',
                              padding: '8px 12px'
                            }}
                          >
                            {page}
                          </Button>
                        ))}
                      </div>
                      <Button
                        type="button"
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                        style={{ 
                          background: 'rgba(0, 0, 0, 0.3)',
                          border: '1px solid var(--border-color)'
                        }}
                      >
                        下一页 →
                      </Button>
                    </div>
                  </>
                ) : (
                  <EmptyState>
                    <h3>暂无成片</h3>
                    <p>请先创建数字人视频</p>
                  </EmptyState>
                )}
              </Card>
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* 视频预览模态框 */}
        {isVideoModalOpen && selectedVideo && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}
            onClick={() => setIsVideoModalOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              style={{
                background: 'rgba(0, 0, 0, 0.9)',
                border: '1px solid var(--border-color)',
                borderRadius: '12px',
                padding: '20px',
                maxWidth: '800px',
                width: '90%',
                maxHeight: '80%',
                overflow: 'auto'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ color: 'var(--text-primary)', margin: 0 }}>{selectedVideo.title}</h3>
                <Button
                  type="button"
                  onClick={() => setIsVideoModalOpen(false)}
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid var(--border-color)',
                    padding: '8px 16px'
                  }}
                >
                  ✕ 关闭
                </Button>
              </div>
              <div style={{ marginBottom: '20px' }}>
                <div style={{ position: 'relative', width: '100%' }}>
                  <video 
                    src={selectedVideo.video_url} 
                    controls 
                    autoPlay 
                    style={{
                      width: '100%',
                      border: '1px solid var(--border-color)',
                      borderRadius: '8px'
                    }}
                    onError={(e) => {
                      console.error('视频加载失败:', e);
                      const errorMessage = document.createElement('div');
                      errorMessage.textContent = '视频加载失败，请检查网络连接或视频链接';
                      errorMessage.style = {
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        background: 'rgba(0, 0, 0, 0.8)',
                        color: 'white',
                        padding: '16px',
                        borderRadius: '8px',
                        textAlign: 'center'
                      };
                      e.target.parentElement.appendChild(errorMessage);
                    }}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--text-secondary)', fontSize: '14px' }}>
                <div>数字人: {selectedVideo.digital_human}</div>
                <div>时长: {selectedVideo.duration}秒</div>
              </div>
            </motion.div>
          </motion.div>
        )}
      {videoPlayer.isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(0, 0, 0, 0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(10px)'
          }}
          onClick={closeVideoPlayer}
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            style={{
              width: '90%',
              maxWidth: '900px',
              background: '#1a1a2e',
              borderRadius: '16px',
              overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
              position: 'relative'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '15px 20px',
              background: 'rgba(0, 0, 0, 0.3)',
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <h3 style={{ margin: 0, color: '#00f0ff', fontSize: '18px' }}>🎬 {videoPlayer.title}</h3>
              <button
                onClick={closeVideoPlayer}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#fff',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '5px 10px',
                  borderRadius: '5px',
                  transition: 'background 0.3s ease'
                }}
                onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.1)'}
                onMouseLeave={(e) => e.target.style.background = 'transparent'}
              >
                ✕
              </button>
            </div>
            <div style={{
              padding: '20px',
              background: '#000'
            }}>
              <video
                controls
                autoPlay
                style={{
                  width: '100%',
                  maxHeight: '70vh',
                  borderRadius: '8px',
                  outline: 'none'
                }}
              >
                <source src={videoPlayer.videoUrl} type="video/mp4" />
                您的浏览器不支持视频播放
              </video>
            </div>
            <div style={{
              padding: '15px 20px',
              background: 'rgba(0, 0, 0, 0.3)',
              display: 'flex',
              justifyContent: 'flex-end'
            }}>
              <a
                href={videoPlayer.videoUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: '#00f0ff',
                  textDecoration: 'none',
                  padding: '8px 16px',
                  borderRadius: '5px',
                  background: 'rgba(0, 240, 255, 0.1)',
                  border: '1px solid rgba(0, 240, 255, 0.3)',
                  fontSize: '14px',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(0, 240, 255, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(0, 240, 255, 0.1)';
                }}
              >
                在新标签页打开
              </a>
            </div>
          </motion.div>
        </motion.div>
      )}
      </PageContainer>
    </Layout>
  );
};

export default ClonePage;
